import { inject, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { GetStartedService as BCGetStartedService } from '@vendasta/business-center';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { editionIds, partnerId, productIds } from '../../globals';
import { AccountGroup, AccountGroupService } from '../account-group';
import {
  GetTrackerResponseInterface,
  TrackersInterface,
} from '@vendasta/business-center/lib/_internal/interfaces/api.interface';
import { AppConfigService } from '../app-config.service';
import { AuthService } from '../auth.service';
import { ImageService } from '../core/image.service';
import { PageAccessService, PageId } from '../page-access';
import { PartnerMarketConfigService } from '../partner-market-config';
import { SocialConnectionsService } from '../social-connections/social-connections.service';
import { FeatureFlagService } from '../core/feature-flag.service';

import { SetupCardConfig } from './setup-card/setup-card.component';
import { EnvironmentService } from '@galaxy/core';
import {
  ConnectionsApiService,
  ConnectionStatus,
  ListConnectionRequest,
  SupportedContexts,
} from '@vendasta/platform-integrations';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NotificationPreferencesService } from '../notification-settings/notification-preferences/notification-preferences.service';
import {
  a2PStartDate,
  aiWebChatProducts,
  getSkipIds,
  GetStartedConstantsHelper,
  getStartedStepIds,
  SMSProducts,
} from './get-started-constants-helper';
import { ProductService } from '../core/product.service';
import {
  A2PRegistrationApiService,
  GetA2PRegistrationInfoResponse,
  GetLatestA2PRegistrationStatusRequestInterface,
  GetLatestA2PRegistrationStatusResponse,
  ParticipantType,
  SmsService,
} from '@vendasta/smsv2';
import { A2PRegistrationStatus } from '@vendasta/smsv2/lib/_internal/objects/a2pregistration';
import { ConversationApiService } from '@vendasta/conversation';
import { getRegistrationStage, RegistrationStage } from '@galaxy/sms';
import { openAppNotificationSettings } from '@vendasta/shared';
import { getSMSSettingUrl } from '../inbox/inbox.constants';
import { PLATFORM_INTEGRATIONS_CONTEXT_INJECTION_TOKEN$ } from '@galaxy/platform-integrations/shared';

type ProgressInfo = {
  id: string;
  complete$: Observable<boolean>;
};

export interface GettingStartedElement {
  progress_info?: ProgressInfo;
  setupCard?: SetupCardConfig;
  show: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GetStartedService {
  private readonly businessAppName$ = inject(AppConfigService).businessAppName$;
  private readonly partnerMarketConfigService = inject(PartnerMarketConfigService);
  private readonly accountGroupService = inject(AccountGroupService);
  private readonly pageVisibilityService = inject(PageAccessService);
  private readonly socialConnectionsService = inject(SocialConnectionsService);
  private readonly imageService = inject(ImageService);
  private readonly router = inject(Router);
  private readonly getStartedService = inject(BCGetStartedService);
  private readonly authService = inject(AuthService);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly environmentService = inject(EnvironmentService);
  private readonly connectionService = inject(ConnectionsApiService);
  private readonly productService = inject(ProductService);
  private readonly smsApiService = inject(SmsService);
  private readonly a2pRegistrationApiService = inject(A2PRegistrationApiService);
  private readonly notificationPreferencesService = inject(NotificationPreferencesService);
  private readonly conversationApi = inject(ConversationApiService);

  private getStartedConstantsHelper: GetStartedConstantsHelper;

  readonly elements$: Observable<GettingStartedElement[]>;
  readonly setupCompleted$: Observable<boolean>;
  readonly totalTasks$: Observable<number>;
  readonly completedTasks$: Observable<number>;
  private appStateChangeListenerAdded = false;

  readonly featureFlags$ = this.accountGroupService.currentAccountGroup$.pipe(
    switchMap((ag) =>
      this.featureFlagService.checkFeatureFlagsMulti(ag.partnerId, ag.marketId, [
        'customer_journey',
        'route_to_new_integrations',
      ]),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private readonly trackerHistory$$ = new BehaviorSubject<TrackersInterface>({});
  private readonly trackerHistory$ = this.trackerHistory$$.asObservable();

  private readonly a2PRegistrationInfo$ = this.accountGroupService.currentAccountGroupId$.pipe(
    switchMap((accountGroupId) => this.a2pRegistrationApiService.getA2pRegistrationInfo({ ownerId: accountGroupId })),
    catchError(() => of({} as GetA2PRegistrationInfoResponse)),
    map((resp) => resp.a2pRegistrationInfo),
  );

  private readonly a2PRegistrationStatus$ = this.accountGroupService.currentAccountGroupId$.pipe(
    switchMap((accountGroupId) =>
      this.smsApiService
        .getAccountInfo({
          internalId: accountGroupId,
          type: ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
        })
        .pipe(
          map(
            (accountInfo) =>
              ({
                subaccountId: accountInfo?.subaccountId,
                ownerId: accountGroupId,
              }) as GetLatestA2PRegistrationStatusRequestInterface,
          ),
          catchError(() => {
            return of({ subaccountId: '', ownerId: '' } as GetLatestA2PRegistrationStatusRequestInterface);
          }),
        ),
    ),
    switchMap((req) => {
      if (req.subaccountId === '') {
        return of({} as A2PRegistrationStatus);
      }
      return this.a2pRegistrationApiService.getLatestA2pRegistrationStatus(req).pipe(
        catchError(() => {
          return of({} as GetLatestA2PRegistrationStatusResponse);
        }),
        map((resp) => resp.status),
      );
    }),
  );

  constructor(
    @Inject(PLATFORM_INTEGRATIONS_CONTEXT_INJECTION_TOKEN$)
    public readonly context: SupportedContexts,
  ) {
    this.getStartedConstantsHelper = new GetStartedConstantsHelper(this.environmentService.getEnvironment());
    const isConnectionsPageVisible$ = this.pageVisibilityService
      .isPageAccessible$(PageId.social_connections)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    const isIntegrationsPageVisible$ = combineLatest([
      this.pageVisibilityService.isPageAccessible$(PageId.platform_integrations),
      this.featureFlags$,
    ]).pipe(
      map(([visible, flags]) => {
        return visible && flags['route_to_new_integrations'];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    const a2PRegistrationStage$ = combineLatest([this.a2PRegistrationInfo$, this.a2PRegistrationStatus$]).pipe(
      map(([info, status]) => {
        return getRegistrationStage(status, !!info?.submitted && info?.submitted > a2PStartDate);
      }),
    );

    this.getTrackerHistory().subscribe((trackerHistory) => {
      this.trackerHistory$$.next(trackerHistory);
    });

    const isInboxAndSettingsPageVisible$ = combineLatest([
      this.pageVisibilityService.isPageAccessible$(PageId.inbox),
      this.pageVisibilityService.isPageAccessible$(PageId.settings_page),
    ]).pipe(
      map(([inboxVisible, settingsVisible]) => inboxVisible && settingsVisible),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const firstInboxWidget$ = this.accountGroupService.currentAccountGroupId$.pipe(
      switchMap((accountGroupId) =>
        this.conversationApi.listWidgets({ namespace: accountGroupId, options: { widgetTypes: ['inbox-pro'] } }),
      ),
      map((resp) => (resp?.widgets?.length > 0 ? resp.widgets[0] : null)),
      catchError(() => of(null)),
    );

    this.elements$ = combineLatest([
      this.businessAppName$,
      this.partnerMarketConfigService.businessAppConfig$,
      this.accountGroupService.currentAccountGroup$,
      this.productService.activeAccessibleProducts$,
      isConnectionsPageVisible$,
      isIntegrationsPageVisible$,
      isInboxAndSettingsPageVisible$,
      a2PRegistrationStage$,
      firstInboxWidget$,
    ]).pipe(
      map(
        ([
          businessAppName,
          partnerMarketConfig,
          accountGroup,
          activeAccessibleProducts,
          connections,
          integrationVisible,
          inboxVisible,
          a2PRegistrationStage,
          inboxWidget,
        ]) => {
          const agid = accountGroup.accountGroupId;
          const isSMSAvailable = activeAccessibleProducts.some((p) => {
            if (p.productId === productIds.reputationManagement) {
              return p.editionId === editionIds.reputationManagementPremium;
            }
            return SMSProducts.includes(p.productId);
          });
          const isWebChatSkuActive = activeAccessibleProducts.some((p) => aiWebChatProducts.includes(p.productId));
          const isWebChatAvailable = inboxVisible && isWebChatSkuActive;
          const webchatBeenEmbedded = inboxWidget?.embedHistory?.length > 0;
          return [
            this.signUpWidget({
              businessAppName,
              onboardingVideoUrl: partnerMarketConfig.onboardingVideoUrl,
              showVideo: partnerMarketConfig.showOnboardingVideo,
            }),
            this.connectGBPWidget(connections, businessAppName, agid, integrationVisible),
            this.installWebChatWidget(agid, inboxWidget?.widgetId, webchatBeenEmbedded, isWebChatAvailable),
            this.manageIntegrationsWidget(agid, integrationVisible),
            this.setUpSMSWidget(agid, accountGroup, isSMSAvailable, a2PRegistrationStage),
            this.notificationsEnabledWidget(Capacitor.isNativePlatform()),
          ];
        },
      ),
      map((elements) => elements.filter((element) => element.show)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.setupCompleted$ = this.elements$.pipe(
      switchMap((elements) => {
        return combineLatest(
          elements
            .filter((element) => element.progress_info)
            .map((element) => {
              return element.progress_info?.complete$;
            }),
        );
      }),
      map((completed) => completed.every((complete) => complete)),
    );

    this.totalTasks$ = this.elements$.pipe(
      map((elements) => elements.filter((element) => element.progress_info).length),
    );

    this.completedTasks$ = this.elements$.pipe(
      switchMap((elements) => {
        return combineLatest(
          elements
            .filter((element) => element.progress_info)
            .map((element) => {
              return element.progress_info?.complete$;
            }),
        );
      }),
      map((completed) => completed.filter((complete) => complete).length),
    );
  }

  private signUpWidget({
    businessAppName,
    onboardingVideoUrl,
    showVideo,
  }: {
    businessAppName: string;
    onboardingVideoUrl: string;
    showVideo: boolean;
  }): GettingStartedElement {
    return {
      progress_info: {
        id: getStartedStepIds.signUp,
        complete$: of(true),
      },
      setupCard: {
        id: getStartedStepIds.signUp,
        title: {
          key: 'GET_STARTED.SETUP_CARDS.SIGN_UP.TITLE',
          variables: { business_app_name: businessAppName },
        },
        description: {
          key: 'GET_STARTED.SETUP_CARDS.SIGN_UP.DESCRIPTION',
          variables: { business_app_name: businessAppName },
        },
        completed$: of(true),
        videoUrl: onboardingVideoUrl,
        showVideo: showVideo,
      },
      show: true,
    };
  }

  private connectGBPWidget(
    showConnections: boolean,
    businessAppName: string,
    accountGroupId: string,
    integrationVisible: boolean,
  ): GettingStartedElement {
    const completed$ = combineLatest([this.trackerHistory$, this.socialConnectionsService.gmbConnected$]).pipe(
      map(([trackerHistory, gmbConnected]) => trackerHistory?.skippedGbpConnection || gmbConnected),
    );

    return {
      progress_info: {
        id: getStartedStepIds.gbpConnection,
        complete$: completed$,
      },
      setupCard: {
        id: getStartedStepIds.gbpConnection,
        title: {
          key: 'GET_STARTED.SETUP_CARDS.CONNECT_GMB.TITLE',
          variables: { business_app_name: businessAppName },
        },
        description: {
          key: 'GET_STARTED.SETUP_CARDS.CONNECT_GMB.DESCRIPTION',
        },
        imageUrl: this.imageService.getImageSrc(`get-started/onboarding-gbp.svg`),
        imageAlt: '',
        actions: [
          {
            text: 'GET_STARTED.SETUP_CARDS.CONNECT_GMB.ACTION',
            onClick: () => this.navigateToGMB(this.router, accountGroupId, integrationVisible),
            color: 'primary',
          },
        ],
        completed$: completed$,
      },
      show: showConnections || integrationVisible,
    };
  }

  private installWebChatWidget(
    accountGroupId: string,
    widgetId: string,
    beenEmbedded: boolean,
    isWebChatAvailable: boolean,
  ): GettingStartedElement {
    const complete$ = this.trackerHistory$.pipe(
      map((trackerHistory) => trackerHistory?.skippedWebchatSetup || beenEmbedded),
    );
    const destURL = widgetId
      ? `/account/location/${accountGroupId}/settings/widgets/${widgetId}/edit`
      : `/account/location/${accountGroupId}/settings`;
    return {
      progress_info: {
        id: getStartedStepIds.webchatSetup,
        complete$: complete$,
      },
      setupCard: {
        id: getStartedStepIds.webchatSetup,
        title: {
          key: 'GET_STARTED.SETUP_CARDS.WEBCHAT_SETUP.TITLE',
        },
        description: {
          key: 'GET_STARTED.SETUP_CARDS.WEBCHAT_SETUP.DESCRIPTION',
        },
        imageUrl: this.imageService.getImageSrc(`get-started/onboarding-webchat.svg`),
        imageAlt: '',
        actions: [
          {
            text: 'GET_STARTED.SETUP_CARDS.WEBCHAT_SETUP.ACTION',
            onClick: () => this.router.navigateByUrl(destURL),
            color: 'primary',
          },
        ],
        completed$: complete$,
      },
      show: isWebChatAvailable,
    };
  }

  private manageIntegrationsWidget(accountGroupId: string, integrationVisible: boolean): GettingStartedElement {
    const connected$ = this.connectionService
      .list(
        new ListConnectionRequest({
          namespace: accountGroupId,
          filters: {
            context: this.context,
          },
          pagingOptions: {
            pageSize: 100,
          },
        }),
      )
      .pipe(
        map((connections) => {
          return connections.connections?.filter(
            (connection) =>
              !this.getStartedConstantsHelper.getExclusionList().includes(connection.integrationId) &&
              connection.status === ConnectionStatus.CONNECTED,
          );
        }),
        map((connections) => connections.length > 0),
        catchError(() => of(false)),
      );

    const completed$ = combineLatest([this.trackerHistory$, connected$]).pipe(
      map(([trackerHistory, connected]) => trackerHistory?.skippedIntegrationConnection || connected),
    );

    return {
      progress_info: {
        id: getStartedStepIds.integrationConnection,
        complete$: completed$,
      },
      setupCard: {
        id: getStartedStepIds.integrationConnection,
        title: {
          key: 'GET_STARTED.SETUP_CARDS.MANAGE_INTEGRATION.TITLE',
        },
        description: {
          key: 'GET_STARTED.SETUP_CARDS.MANAGE_INTEGRATION.DESCRIPTION',
        },
        imageUrl: this.imageService.getImageSrc(`get-started/onboarding-connect.svg`),
        imageAlt: '',
        actions: [
          {
            text: 'GET_STARTED.SETUP_CARDS.MANAGE_INTEGRATION.ACTION',
            onClick: () => this.router.navigateByUrl(`/account/location/${accountGroupId}/settings/integrations`),
            color: 'primary',
          },
        ],
        completed$: completed$,
      },
      show: integrationVisible,
    };
  }

  private setUpSMSWidget(
    accountGroupId: string,
    accountGroup: AccountGroup,
    isSmsAvailable: boolean,
    a2PStage: RegistrationStage,
  ): GettingStartedElement {
    const SMSWidget = {
      progress_info: {
        id: getStartedStepIds.smsRegistration,
        complete$: of(false),
      },
      setupCard: {
        id: getStartedStepIds.smsRegistration,
        title: {
          key: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.TITLE',
        },
        description: {
          key: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.DESCRIPTION',
        },
        imageUrl: this.imageService.getImageSrc(`get-started/onboarding-sms.svg`),
        imageAlt: '',
        actions: [],
        completed$: of(false),
      },
      show: isSmsAvailable,
    } as GettingStartedElement;

    const agCountry = accountGroup.country?.toUpperCase();

    if (agCountry === 'CA') {
      SMSWidget.setupCard.completed$ = of(true);
      SMSWidget.progress_info.complete$ = of(true);
      SMSWidget.setupCard.actions.push({
        text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.ACTION',
        onClick: () => this.router.navigateByUrl(`/account/location/${accountGroupId}/settings/inbox`),
        color: 'primary',
      });
      return SMSWidget;
    } else if (agCountry === 'US') {
      const skipped$ = this.trackerHistory$.pipe(map((trackerHistory) => trackerHistory?.skippedSmsRegistration));
      switch (a2PStage) {
        case RegistrationStage.NotSubmitted:
          SMSWidget.setupCard.actions.push({
            text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.ACTION',
            onClick: () => this.router.navigateByUrl(getSMSSettingUrl(accountGroupId)),
            color: 'primary',
          });
          SMSWidget.setupCard.completed$ = skipped$;
          SMSWidget.progress_info.complete$ = skipped$;
          break;
        case RegistrationStage.ReviewingInfo:
          SMSWidget.setupCard.actions.push({
            text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.ACTION_IN_PROGRESS',
            onClick: () => this.router.navigateByUrl(getSMSSettingUrl(accountGroupId)),
            color: 'primary',
          });
          SMSWidget.setupCard.completed$ = skipped$;
          SMSWidget.progress_info.complete$ = skipped$;
          SMSWidget.setupCard.alert = {
            text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.INFO_IN_PROGRESS',
            type: 'info',
          };
          break;
        case RegistrationStage.RegistrationComplete:
          SMSWidget.setupCard.completed$ = of(true);
          SMSWidget.progress_info.complete$ = of(true);
          break;
        default:
          SMSWidget.setupCard.actions.push({
            text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.ACTION_NEED_ACTION',
            onClick: () => this.router.navigateByUrl(getSMSSettingUrl(accountGroupId)),
            color: 'primary',
          });
          SMSWidget.setupCard.completed$ = skipped$;
          SMSWidget.progress_info.complete$ = skipped$;
          SMSWidget.setupCard.alert = {
            text: 'GET_STARTED.SETUP_CARDS.REGISTER_SMS.ALERT_NEED_ACTION',
            type: 'error',
          };
          break;
      }

      return SMSWidget;
    }

    return { show: false };
  }

  private notificationsEnabledWidget(isMobile: boolean): GettingStartedElement {
    const enabled$$ = new BehaviorSubject<boolean>(false);
    const enabled$ = enabled$$.asObservable();
    this.notificationPreferencesService.pushNotificationsEnabled().then((enabled) => {
      enabled$$.next(enabled);
    });

    const completed$ = combineLatest([this.trackerHistory$, enabled$]).pipe(
      map(([trackerHistory, notificationEnabled]) => trackerHistory?.skippedMobileNotification || notificationEnabled),
    );

    return {
      show: isMobile,
      progress_info: {
        id: getStartedStepIds.mobileNotification,
        complete$: completed$,
      },
      setupCard: {
        id: getStartedStepIds.mobileNotification,
        title: {
          key: 'GET_STARTED.WIDGET.NOTIFICATIONS.TITLE',
        },
        description: {
          key: 'GET_STARTED.WIDGET.NOTIFICATIONS.DESCRIPTION',
        },
        imageUrl: this.imageService.getImageSrc(`get-started/onboarding-notifications.svg`),
        actions: [
          {
            text: 'GET_STARTED.WIDGET.NOTIFICATIONS.ACTION',
            onClick: () => {
              if (!this.appStateChangeListenerAdded) {
                this.appStateChangeListenerAdded = true;
                App.addListener('appStateChange', async (state) => {
                  if (state.isActive) {
                    const enabled = await this.notificationPreferencesService.pushNotificationsEnabled();
                    enabled$$.next(enabled);
                    if (enabled) {
                      await this.notificationPreferencesService.registerForPushNotifications();
                    }
                  }
                });
              }
              openAppNotificationSettings();
            },
            color: 'primary',
          },
        ],
        completed$: completed$,
      },
    };
  }

  getTrackerHistory(): Observable<TrackersInterface> {
    return this.getStartedService.getTracker(partnerId, this.authService.userId()).pipe(
      map((response: GetTrackerResponseInterface) => response.trackers),
      catchError(() => of(null)),
      take(1),
    );
  }

  setTracker(tracker: string): void {
    this.getStartedService
      .setTracker(partnerId, this.authService.userId(), getSkipIds(tracker))
      .pipe(
        catchError(() => of(null)),
        tap((resp) => {
          if (resp?.success) {
            const currentTrackers = this.trackerHistory$$.getValue() ?? {};

            switch (tracker) {
              case getStartedStepIds.gbpConnection:
                currentTrackers.skippedGbpConnection = true;
                break;
              case getStartedStepIds.integrationConnection:
                currentTrackers.skippedIntegrationConnection = true;
                break;
              case getStartedStepIds.smsRegistration:
                currentTrackers.skippedSmsRegistration = true;
                break;
              case getStartedStepIds.mobileNotification:
                currentTrackers.skippedMobileNotification = true;
                break;
              case getStartedStepIds.webchatSetup:
                currentTrackers.skippedWebchatSetup = true;
                break;
              default:
                break;
            }

            this.trackerHistory$$.next(currentTrackers);
          }
        }),
        take(1),
      )
      .subscribe();
  }

  navigateToGMB(router: Router, accountGroupId: string, integrationVisible: boolean): void {
    if (integrationVisible) {
      router.navigateByUrl(
        `/account/location/${accountGroupId}/settings/integrations/${this.getStartedConstantsHelper.getGbpIntegrationId()}`,
      );
      return;
    }
    router.navigateByUrl(`/account/location/${accountGroupId}/settings/connections`);
  }
}
